"use client";

import { useTranslations } from "next-intl";
import {
  ATTACH_PAGE_SIZE_OPTIONS,
  type AttachPageSize,
} from "@/components/admin/attach-picker/types";
import { ADMIN_TABLE_PAGINATION_DEFAULTS } from "@/components/admin/admin-table-utils";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { cn } from "@/lib/utils";

export function AttachBatchBar({
  total,
  page,
  totalPages,
  pageSize,
  selectedCount,
  allSelected,
  selectionVisible,
  onToggleSelectionMode,
  onMove,
  onDelete,
  onPageChange,
  onPageSizeChange,
  disabled,
  className,
}: {
  total: number;
  page: number;
  totalPages: number;
  pageSize: AttachPageSize;
  selectedCount: number;
  allSelected: boolean;
  selectionVisible: boolean;
  onToggleSelectionMode: () => void;
  onMove: () => void;
  onDelete: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: AttachPageSize) => void;
  disabled?: boolean;
  className?: string;
}) {
  const t = useTranslations("site.attach");

  return (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-2 bg-muted/20 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {selectionVisible ? (
          <label className="inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={allSelected}
              disabled={disabled || total === 0}
              aria-label={t("selectAll")}
              className="size-4 cursor-pointer rounded border-input"
              onChange={onToggleSelectionMode}
            />
          </label>
        ) : null}
        {selectedCount > 0 ? (
          <>
            <button
              type="button"
              disabled={disabled}
              className="cursor-pointer text-muted-foreground hover:text-primary disabled:cursor-not-allowed disabled:text-muted-foreground"
              onClick={onMove}
            >
              {t("move")}
            </button>
            <button
              type="button"
              disabled={disabled}
              className="cursor-pointer text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:text-muted-foreground"
              onClick={onDelete}
            >
              {t("delete")}
            </button>
          </>
        ) : null}
      </div>

      <DataTablePagination
        pageIndex={Math.max(page - 1, 0)}
        pageCount={Math.max(totalPages, 1)}
        pageSize={pageSize}
        filteredTotal={total}
        pageSizeOptions={[...ATTACH_PAGE_SIZE_OPTIONS]}
        showPageSizeSelector={ADMIN_TABLE_PAGINATION_DEFAULTS.showPageSizeSelector}
        showPageJumpInput={ADMIN_TABLE_PAGINATION_DEFAULTS.showPageJumpInput}
        disabled={disabled}
        onPageIndexChange={(pageIndex) => onPageChange(pageIndex + 1)}
        onPageSizeChange={(nextPageSize) =>
          onPageSizeChange(nextPageSize as AttachPageSize)
        }
      />
    </div>
  );
}
